const POLICY_LABELS = {
  must_use: '必须写入',
  can_use: '可写入',
  backend_only: '后台词',
  ads_only: '广告测试',
  forbidden: '禁用',
};

const SEMANTIC_GROUP_LABELS = {
  physical: '物理属性',
  theme: '当前主题',
  shared: '共同意图',
  pending: '待核验流量词',
  blocked: '拦截词',
};

const ROLE_LABELS = {
  main_competitor: '主竞品',
  traffic_reference: '流量参考',
  copy_reference: '文案参考',
  image_reference: '图片参考',
  excluded: '排除',
};

function fieldsForPolicy(policy) {
  return {
    preferredFields: policy === 'must_use' ? ['title', 'bullets']
      : policy === 'can_use' ? ['bullets', 'description']
        : policy === 'backend_only' ? ['searchTerms']
          : policy === 'ads_only' ? ['ads']
            : [],
    blockedFields: policy === 'forbidden' ? ['title', 'bullets', 'description', 'searchTerms']
      : policy === 'backend_only' ? ['title']
        : policy === 'ads_only' ? ['title', 'bullets', 'description']
          : [],
  };
}

function countBy(items, key) {
  const counts = {};
  for (const item of items || []) {
    const value = item?.[key];
    if (value) counts[value] = (counts[value] || 0) + 1;
  }
  return counts;
}

/**
 * Owns Step2's keyword policy and competitor-role editing plus the matching
 * business-context panel. The entry only supplies state and DOM adapters.
 */
export function createBusinessContextController({
  select,
  getState,
  setState,
  createElement,
  onKeywordPolicyChanged = () => {},
}) {
  function updateKeywordPolicy(keyword, policy) {
    const list = getState('keywordUsePolicies') || [];
    const normalizedKeyword = String(keyword || '').toLowerCase();
    let matched = false;
    const fields = fieldsForPolicy(policy);
    const next = list.map((item) => {
      if (String(item.keyword || '').toLowerCase() !== normalizedKeyword) return item;
      matched = true;
      return {
        ...item,
        policy,
        ...fields,
        reason: `运营手动调整为：${POLICY_LABELS[policy] || policy}`,
        manuallyAdjusted: true,
      };
    });
    if (!matched && normalizedKeyword) {
      next.push({
        schemaVersion: 'keyword-use-policy.v1',
        keyword,
        normalizedKeyword,
        policy,
        ...fields,
        reason: `运营手动调整为：${POLICY_LABELS[policy] || policy}`,
        evidence: { sources: ['manual_override'], sourceAsins: [] },
        risks: [],
        score: 0,
        manuallyAdjusted: true,
      });
    }
    setState('keywordUsePolicies', next);
    const businessContext = getState('businessContext') || {};
    setState('businessContext', { ...businessContext, keywordUsePolicies: next });
    onKeywordPolicyChanged(next);
  }

  function updateCompetitorRole(asin, role) {
    const list = getState('competitorRoles') || [];
    const normalizedAsin = String(asin || '').toUpperCase();
    let matched = false;
    const next = list.map((item) => {
      if (String(item.asin || '').toUpperCase() !== normalizedAsin) return item;
      matched = true;
      return {
        ...item,
        role,
        roleLabel: ROLE_LABELS[role] || role,
        manuallyAdjusted: true,
        reason: `运营手动调整为：${ROLE_LABELS[role] || role}`,
      };
    });
    if (!matched && normalizedAsin) {
      next.push({
        schemaVersion: 'competitor-role-plan.v1',
        asin: normalizedAsin,
        role,
        roleLabel: ROLE_LABELS[role] || role,
        allowedInfluence: [],
        forbiddenInfluence: [],
        similarity: {},
        reason: `运营手动调整为：${ROLE_LABELS[role] || role}`,
        manuallyAdjusted: true,
      });
    }
    setState('competitorRoles', next);
    const businessContext = getState('businessContext') || {};
    setState('businessContext', { ...businessContext, competitorRoles: next });
  }

  function createPolicySelect(keyword, currentPolicy) {
    const policySelect = createElement('select', 'keyword-policy-select');
    policySelect.dataset.keyword = String(keyword || '');
    policySelect.title = '调整该词在 Listing 中的使用策略';
    policySelect.append(...Object.entries(POLICY_LABELS).map(([value, label]) => {
      const option = createElement('option', '', label);
      option.value = value;
      option.selected = value === currentPolicy;
      return option;
    }));
    policySelect.addEventListener('change', () => updateKeywordPolicy(policySelect.dataset.keyword, policySelect.value));
    return policySelect;
  }

  function render() {
    const panel = select('#business-context-panel');
    if (!panel) return;
    const policies = getState('keywordUsePolicies') || [];
    const roles = getState('competitorRoles') || [];
    if (!policies.length && !roles.length) {
      panel.hidden = true;
      panel.replaceChildren();
      return;
    }
    panel.hidden = false;
    const policyCounts = countBy(policies, 'policy');
    const semanticGroups = getState('businessContext')?.keywordSemanticGroups || {};
    const roleCounts = countBy(roles, 'role');
    const mustUse = policies.filter((item) => item.policy === 'must_use').slice(0, 8);
    const forbidden = policies.filter((item) => item.policy === 'forbidden').slice(0, 8);
    const countChip = (count, label) => {
      const chip = createElement('span', 'chip');
      chip.append(createElement('strong', '', count), document.createTextNode(` ${label}`));
      return chip;
    };
    const header = createElement('div', 'business-context-header');
    const headerCopy = document.createElement('div');
    headerCopy.append(
      createElement('div', 'business-context-title', '策略配置与竞品角色'),
      createElement('div', 'business-context-subtitle', '这里是 AI 帮选的策略来源，不是另一套推荐结果；会随 Step3 生成请求传给后端，用于约束 Listing 生成。'),
    );
    header.append(headerCopy);

    const policyColumn = document.createElement('div');
    const policyChips = createElement('div', 'business-context-chips');
    policyChips.append(...Object.entries(POLICY_LABELS).map(([key, label]) => countChip(policyCounts[key] || 0, label)));
    policyColumn.append(createElement('div', 'business-context-label', '关键词准入'), policyChips);
    const semanticChips = createElement('div', 'business-context-chips');
    const semanticChipNodes = Object.entries(SEMANTIC_GROUP_LABELS)
      .map(([key, label]) => [key, label, Array.isArray(semanticGroups[key]) ? semanticGroups[key] : []])
      .filter(([, , terms]) => terms.length)
      .map(([, label, terms]) => countChip(terms.length, label));
    if (semanticChipNodes.length) {
      semanticChips.append(...semanticChipNodes);
      policyColumn.append(createElement('div', 'business-context-label', '语义预处理'), semanticChips);
    }
    for (const [items, tone] of [[mustUse, 'good'], [forbidden, 'risk']]) {
      if (!items.length) continue;
      const tags = createElement('div', 'business-context-tags');
      tags.append(...items.map((item) => createElement('span', `business-tag ${tone}`, item.keyword)));
      policyColumn.append(tags);
    }

    const roleColumn = document.createElement('div');
    const roleChips = createElement('div', 'business-context-chips');
    const roleChipNodes = Object.entries(ROLE_LABELS)
      .filter(([key]) => roleCounts[key])
      .map(([key, label]) => countChip(roleCounts[key], label));
    roleChips.append(...(roleChipNodes.length ? roleChipNodes : [createElement('span', 'chip', '暂无角色')]));
    const roleList = createElement('div', 'business-role-list');
    roleList.append(...roles.map((item) => {
      const row = createElement('div', 'business-role-row');
      const roleSelect = createElement('select', 'competitor-role-select');
      roleSelect.dataset.asin = String(item.asin || '');
      roleSelect.append(...Object.entries(ROLE_LABELS).map(([value, label]) => {
        const option = createElement('option', '', label);
        option.value = value;
        option.selected = value === item.role;
        return option;
      }));
      roleSelect.addEventListener('change', () => updateCompetitorRole(roleSelect.dataset.asin, roleSelect.value));
      row.append(createElement('span', 'business-role-asin', item.asin), roleSelect);
      return row;
    }));
    roleColumn.append(createElement('div', 'business-context-label', '竞品角色'), roleChips, roleList);
    const grid = createElement('div', 'business-context-grid');
    grid.append(policyColumn, roleColumn);
    panel.replaceChildren(header, grid);
  }

  return { createPolicySelect, render, updateKeywordPolicy, updateCompetitorRole };
}
